'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState, LoginCredentials, RegisterData } from '@/types/user';
import { InUser } from '@/models/User';
import { authApi } from '@/lib/api/auth-api';
import { saveTokenCookie, saveUserCookie, clearAuthCookies } from '@/lib/cookie-utils';
import { useRouter } from 'next/navigation';

interface AuthContextType extends AuthState {
    login: (credentials: LoginCredentials) => Promise<void>;
    register: (data: RegisterData) => Promise<User>;
    logout: () => void;
    verifyEmail: (email: string, otp: string) => Promise<boolean>;
    updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function mapApiUserToUser(apiUser: InUser): User {
    return {
        id: String(apiUser.id),
        username: apiUser.username,
        email: apiUser.email,
        fullName: apiUser.name || apiUser.full_name,
        phone: apiUser.phone || undefined,
        role: (apiUser.role as any) || 'general user',
        scope: (apiUser.scope as any) || '',
        avatar: apiUser.profile_picture || apiUser.avatar || undefined,
        emailVerified: true,
        createdAt: apiUser.created_at || new Date().toISOString(),
        status: apiUser.status,
        last_login: apiUser.last_login || null,
    };
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        user: null,
        isAuthenticated: false,
        isLoading: true,
    });

    const router = useRouter();

    // 🔥 INIT AUTH (SINGLE SOURCE: TOKEN)
    useEffect(() => {
        let mounted = true;

        const initAuth = async () => {
            try {
                const token = localStorage.getItem('key');

                if (!token) {
                    if (!mounted) return;
                    setState({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                    });
                    return;
                }

                try {
                    const me = await authApi.whoami();

                    if (!mounted) return;

                    setState({
                        user: mapApiUserToUser(me.user),
                        isAuthenticated: true,
                        isLoading: false,
                    });
                } catch {
                    if (!mounted) return;

                    localStorage.removeItem('key');
                    clearAuthCookies();

                    setState({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                    });
                }
            } catch {
                if (!mounted) return;

                setState({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                });
            }
        };

        initAuth();
        return () => {
            mounted = false;
        };
    }, []);

    // 🔐 LOGIN
    const login = async (credentials: LoginCredentials): Promise<void> => {
        const response = await authApi.login({
            username: credentials.emailOrUsername,
            password: credentials.password,
        });

        if (!response.success || !response.user) {
            throw new Error(response.message || 'Login failed');
        }

        const token = response.key;

        if (token) {
            localStorage.setItem('key', token);
        }

        // The login endpoint forces `role=GUEST`, so its `user` payload doesn't
        // reflect an existing EO/FO registration. Refetch via whoami (same call
        // used on page load) to get the user's actual role/scope.
        const me = await authApi.whoami();
        const user = mapApiUserToUser(me.user);

        if (token) {
            saveTokenCookie(token, credentials.rememberMe);
            saveUserCookie(user, credentials.rememberMe);
        }

        setState({
            user,
            isAuthenticated: true,
            isLoading: false,
        });
    };

    // 📝 REGISTER
    const register = async (data: RegisterData): Promise<User> => {
        const response = await authApi.register({
            username: data.username,
            name: data.fullName,
            email: data.email,
            password: data.password,
            phone: data.phone,
        });

        if (!response.success || !response.user) {
            throw new Error(response.message || 'Registration failed');
        }

        const user = mapApiUserToUser(response.user);
        const token = response.key;

        // If backend returns a token (e.g. for SSO or auto-login)
        if (token) {
            localStorage.setItem('key', token);
            saveTokenCookie(token, false);
            saveUserCookie(user, false);

            setState({
                user,
                isAuthenticated: true,
                isLoading: false,
            });
        }

        return user;
    };

    // 🚪 LOGOUT
    const logout = () => {
        localStorage.removeItem('key');
        clearAuthCookies();

        setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
        });

        router.push('/');
    };

    // 📧 VERIFY EMAIL
    const verifyEmail = async (email: string, otp: string): Promise<boolean> => {
        const response = await authApi.verifyEmail(email, otp);

        if (response.success && response.user) {
            const user = mapApiUserToUser(response.user);
            const token = response.key;

            if (token) {
                localStorage.setItem('key', token);
                saveTokenCookie(token, false);
                saveUserCookie(user, false);
            }

            setState({
                user,
                isAuthenticated: true,
                isLoading: false,
            });

            return true;
        }

        return false;
    };

    // 🔄 UPDATE USER
    const updateUser = (user: User) => {
        setState(prev => ({
            ...prev,
            user,
        }));

        saveUserCookie(user, true);
    };

    return (
        <AuthContext.Provider
            value={{
                ...state,
                login,
                register,
                logout,
                verifyEmail,
                updateUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}